import { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useModelService } from 'services/model/model.service';
import { Model, PerformanceEntry } from 'pages/Benchmarking/performance/model';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { useAppStore } from 'hooks/state/useAppStore';
import { createPerformanceMetrics, toSum } from '../helpers/metricsUtils';
import { formatRange } from '../helpers/formatUtils';
import { useBenchmarkDataResult } from 'pages/Benchmarking/queryHooks';
import { getErrorMessage } from 'utils/getErrorMessage';

interface PerformanceData {
  gmvYoY: Model;
  gmvPoP: Model;
  returnRate: Model;
  cancellationRate: Model;
}

export function usePerformanceData() {
  const intl = useIntl();
  const { getMeasures } = useModelService();
  const { benchmark, benchmarkingDateRange } = useAppStore();
  const { name } = useContext(TenantConfigContext);
  const [data, setData] = useState<PerformanceData>();

  const [ordGmvVal, retQtyRateOD, cncQtyRate] = getMeasures(['ordGmvVal', 'retQtyRateExpOD', 'cncQtyRate']);

  const { data: dataResult, isPending, isError, error } = useBenchmarkDataResult('performance');

  const getDateRange = (timeSeries: Model['timeSeries']) => {
    const from = timeSeries[0]?.dateRange?.fromDate;
    const to = timeSeries[timeSeries.length - 1]?.dateRange?.toDate;
    return formatRange(from, to, benchmarkingDateRange.temporalType, intl.locale);
  };
  const performanceData: PerformanceData | undefined = useMemo(() => {
    if (!dataResult) return undefined;

    const gmvYoY = createPerformanceMetrics(
      dataResult,
      'timeSeriesYoY',
      benchmark,
      ordGmvVal,
      benchmarkingDateRange.temporalType,
      intl
    );
    const gmvPoP = createPerformanceMetrics(
      dataResult,
      'timeSeriesPoP',
      benchmark,
      ordGmvVal,
      benchmarkingDateRange.temporalType,
      intl
    );
    const returnRate = createPerformanceMetrics(
      dataResult,
      'timeSeriesPoP',
      benchmark,
      retQtyRateOD,
      benchmarkingDateRange.temporalType,
      intl
    );
    const cancellationRate = createPerformanceMetrics(
      dataResult,
      'timeSeriesPoP',
      benchmark,
      cncQtyRate,
      benchmarkingDateRange.temporalType,
      intl
    );

    gmvPoP.tenantValue = toSum(gmvPoP.timeSeries, (e: PerformanceEntry) => e.tenantValue);
    gmvPoP.tenantCompareValue = toSum(gmvPoP.timeSeries, (e: PerformanceEntry) => e.tenantCompareValue);
    gmvPoP.benchmarkValue = toSum(gmvPoP.timeSeries, (e: PerformanceEntry) => e.benchmarkValue);
    gmvPoP.benchmarkCompareValue = toSum(gmvPoP.timeSeries, (e: PerformanceEntry) => e.benchmarkCompareValue);
    gmvPoP.tenantGrowth =
      toSum(gmvPoP.timeSeries, (e: PerformanceEntry) =>
        e.tenantCompareValue ? e.tenantValue / e.tenantCompareValue - 1 : 0
      ) / 12;
    gmvPoP.benchmarkGrowth =
      toSum(gmvPoP.timeSeries, (e: PerformanceEntry) =>
        e.benchmarkCompareValue ? e.benchmarkValue / e.benchmarkCompareValue - 1 : 0
      ) / 12;

    const tenantYoY = gmvYoY.tenantValue / (gmvYoY.tenantCompareValue ?? 1) - 1;
    const benchmarkYoY = gmvYoY.benchmarkValue / (gmvYoY.benchmarkCompareValue ?? 1) - 1;
    const yoyGap = gmvYoY.tenantValue / ((gmvYoY.tenantCompareValue ?? 0) * (1 + benchmarkYoY)) - 1;
    const yoyRange = getDateRange(gmvYoY.timeSeries);

    gmvYoY.metrics = [
      {
        tenantName: name,
        subtitle: intl.formatMessage({
          id: 'benchmarking.performance.kpi.result.gmv_growth',
        }),
        value: tenantYoY,
        unit: MeasureUnitCode.PERCENT,
        timeRange: yoyRange,
        isTenant: true,
      },
      {
        tenantName: intl.formatMessage({
          id: `benchmarking.benchmarks.${benchmark}`,
        }),
        subtitle: intl.formatMessage({
          id: 'benchmarking.performance.kpi.result.gmv_growth',
        }),
        value: benchmarkYoY,
        unit: MeasureUnitCode.PERCENT,
        timeRange: yoyRange,
      },
      {
        title: intl.formatMessage({
          id: 'benchmarking.performance.kpi.result.performance_gap',
        }),
        tenantName: intl.formatMessage({
          id: `benchmarking.benchmarks.${benchmark}`,
        }),
        subtitle: intl.formatMessage({
          id:
            yoyGap > 0
              ? 'benchmarking.performance.kpi.result.positive'
              : 'benchmarking.performance.kpi.result.negative',
        }),
        value: yoyGap,
        unit: MeasureUnitCode.PERCENT,
        hideSign: false,
        isPerformanceGap: true,
      },
    ];

    const lastMonth = gmvPoP?.timeSeries[gmvPoP.timeSeries.length - 1];
    const lastMonthTenantGmv = lastMonth?.tenantValue ?? 0;
    const previousMonthTenantGmv = lastMonth?.tenantCompareValue ?? 0;
    const lastMonthBenchmarkGmv = lastMonth?.benchmarkValue ?? 0;
    const previousMonthBenchmarkGmv = lastMonth?.benchmarkCompareValue ?? 0;
    const momGap =
      lastMonthTenantGmv / (previousMonthTenantGmv * (lastMonthBenchmarkGmv / previousMonthBenchmarkGmv) || 1) - 1;

    gmvPoP.metrics = [
      {
        tenantName: name,
        subtitle: intl.formatMessage({
          id: `benchmarking.performance.kpi.result.pop_growth.${benchmarkingDateRange.temporalType.toLowerCase()}`,
        }),
        value: (lastMonthTenantGmv - previousMonthTenantGmv) / previousMonthTenantGmv,
        unit: MeasureUnitCode.PERCENT,
        timeRange: lastMonth?.label,
        isTenant: true,
      },
      {
        tenantName: intl.formatMessage({
          id: `benchmarking.benchmarks.${benchmark}`,
        }),
        subtitle: intl.formatMessage({
          id: `benchmarking.performance.kpi.result.pop_growth.${benchmarkingDateRange.temporalType.toLowerCase()}`,
        }),
        value: (lastMonthBenchmarkGmv - previousMonthBenchmarkGmv) / previousMonthBenchmarkGmv,
        unit: MeasureUnitCode.PERCENT,
        timeRange: lastMonth?.label,
      },
      {
        title: intl.formatMessage({
          id: 'benchmarking.performance.kpi.result.performance_gap',
        }),
        tenantName: intl.formatMessage({
          id: `benchmarking.benchmarks.${benchmark}`,
        }),
        subtitle: intl.formatMessage({
          id:
            momGap > 0
              ? 'benchmarking.performance.kpi.result.positive'
              : 'benchmarking.performance.kpi.result.negative',
        }),
        value: momGap,
        unit: MeasureUnitCode.PERCENT,
        hideSign: false,
        isPerformanceGap: true,
      },
    ];

    // If peer returnRate peer value is zero, drop leading months with zero values and average the remaining timeSeries
    if (returnRate.benchmarkValue === 0) {
      const series = returnRate.timeSeries;
      const start = series.findIndex(e => (e.benchmarkValue ?? 0) !== 0);
      const validSeries = start >= 0 ? series.slice(start) : series;
      returnRate.benchmarkValue = toSum(validSeries, e => e.benchmarkValue) / (validSeries.length || 1);
    }
    // If peer cnc peer value is zero, drop leading months with zero values and average the remaining timeSeries
    if (cancellationRate.benchmarkValue === 0) {
      const series = cancellationRate.timeSeries;
      const start = series.findIndex(e => (e.benchmarkValue ?? 0) !== 0);
      const validSeries = start >= 0 ? series.slice(start) : series;
      cancellationRate.benchmarkValue = toSum(validSeries, e => e.benchmarkValue) / (validSeries.length || 1);
    }
    const retRange = getDateRange(returnRate.timeSeries);
    const retGap = returnRate.tenantValue - returnRate.benchmarkValue;

    const cancRange = getDateRange(cancellationRate.timeSeries);
    const cancGap = cancellationRate.tenantValue - cancellationRate.benchmarkValue;

    returnRate.metrics = [
      {
        tenantName: name,
        subtitle: intl.formatMessage({
          id: 'benchmarking.performance.kpi.result.returns',
        }),
        value: returnRate.tenantValue,
        unit: MeasureUnitCode.PERCENT,
        hideSign: true,
        isTenant: true,
        timeRange: retRange,
      },
      {
        tenantName: intl.formatMessage({
          id: `benchmarking.benchmarks.${benchmark}`,
        }),
        subtitle: intl.formatMessage({
          id: 'benchmarking.performance.kpi.result.returns',
        }),
        value: returnRate.benchmarkValue,
        unit: MeasureUnitCode.PERCENT,
        hideSign: true,
        timeRange: retRange,
      },
      {
        title: intl.formatMessage({
          id: 'benchmarking.performance.kpi.result.performance_gap',
        }),
        subtitle: intl.formatMessage({
          id:
            retGap < 0
              ? 'benchmarking.performance.kpi.result.positive'
              : 'benchmarking.performance.kpi.result.negative',
        }),
        value: retGap,
        unit: MeasureUnitCode.PERCENT,
        hideSign: false,
        isDiff: true,
        isPerformanceGap: true,
      },
    ];

    cancellationRate.metrics = [
      {
        tenantName: name,
        subtitle: intl.formatMessage({
          id: 'benchmarking.performance.kpi.result.cancellations',
        }),
        value: cancellationRate.tenantValue,
        unit: MeasureUnitCode.PERCENT,
        hideSign: true,
        isTenant: true,
        timeRange: cancRange,
      },
      {
        tenantName: intl.formatMessage({
          id: `benchmarking.benchmarks.${benchmark}`,
        }),
        subtitle: intl.formatMessage({
          id: 'benchmarking.performance.kpi.result.cancellations',
        }),
        value: cancellationRate.benchmarkValue,
        unit: MeasureUnitCode.PERCENT,
        hideSign: true,
        timeRange: cancRange,
      },
      {
        title: intl.formatMessage({
          id: 'benchmarking.performance.kpi.result.performance_gap',
        }),
        subtitle: intl.formatMessage({
          id:
            cancGap < 0
              ? 'benchmarking.performance.kpi.result.positive'
              : 'benchmarking.performance.kpi.result.negative',
        }),
        value: cancGap,
        unit: MeasureUnitCode.PERCENT,
        hideSign: false,
        isDiff: true,
        isPerformanceGap: true,
      },
    ];
    return {
      gmvYoY,
      gmvPoP,
      returnRate,
      cancellationRate,
    };
  }, [dataResult, benchmark, intl, name]);

  useEffect(() => {
    if (performanceData) setData(performanceData);
  }, [performanceData]);

  return {
    ordGmvVal,
    retQtyRateOD,
    cncQtyRate,
    performanceData: data,
    benchmark,
    isLoading: isPending,
    isError,
    errorMessage: getErrorMessage(error),
  };
}
